/**
 * LoadingUI - Manages the loading screen with progress bar and status text
 */

import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('LoadingUI');

export class LoadingUI {
  private container: HTMLElement | null;
  private progressBarFill: HTMLElement | null;
  private percentageText: HTMLElement | null;
  private statusText: HTMLElement | null;
  private logoContainer: HTMLElement | null;
  private defaultBranding: SVGElement | null;
  private logoImg: HTMLImageElement | null;

  constructor() {
    this.container = document.getElementById('loading');
    this.progressBarFill = document.getElementById('loadingProgressBarFill');
    this.percentageText = document.getElementById('loadingPercentage');
    this.statusText = document.getElementById('loadingStatus');
    this.logoContainer = document.getElementById('loadingLogo');
    this.defaultBranding = document.getElementById('loadingDefaultBranding') as unknown as SVGElement;
    this.logoImg = document.getElementById('loadingLogoImg') as HTMLImageElement;
  }

  /**
   * Show the loading screen
   * @param reset Whether to reset progress to 0 (default: false)
   */
  show(reset: boolean = false): void {
    if (this.container) {
      this.container.classList.remove('hidden');
      
      // Reset state only if requested
      if (reset) {
        this.updateProgress(0, 'Initializing...');
      }
    }
  }

  /**
   * Hide the loading screen
   */
  hide(): void {
    if (this.container) {
      this.container.classList.add('hidden');
    }
  }

  /**
   * Update progress and status text
   * @param percent Progress percentage (0-100)
   * @param text Status text to display
   */
  updateProgress(percent: number, text: string): void {
    // Auto-show removed: Loading screen should be controlled explicitly by UIManager
    // if (!this.isVisible && this.container) {
    //   this.show();
    // }

    // Clamp percentage
    const clampedPercent = Math.max(0, Math.min(100, percent));
    
    if (this.progressBarFill) {
      this.progressBarFill.style.width = `${clampedPercent}%`;
    }

    if (this.percentageText) {
      this.percentageText.textContent = `${Math.round(clampedPercent)}%`;
    }

    if (this.statusText) {
      this.statusText.textContent = text;
    }
  }

  /**
   * Show the default XRGallery wordmark (already in HTML, just ensure it's visible)
   * This is the default state - the SVG is shown, custom logo img is hidden
   */
  showDefaultBranding(): void {
    if (!this.logoContainer) return;

    // Show the container
    this.logoContainer.classList.remove('hidden');

    // Show default SVG branding, hide custom logo
    // Use empty string to restore default display (inline for SVG)
    if (this.defaultBranding) {
      this.defaultBranding.style.display = '';
    }
    if (this.logoImg) {
      this.logoImg.style.display = 'none';
    }
  }

  /**
   * Set custom logo for white-label experiences (Pro+ feature)
   * @param logoUrl URL of the logo image
   */
  setLogo(logoUrl: string): void {
    if (!this.logoContainer || !this.logoImg) return;

    // Show container, hide default branding, show custom logo
    this.logoContainer.classList.remove('hidden');

    if (this.defaultBranding) {
      this.defaultBranding.style.display = 'none';
    }

    this.logoImg.src = logoUrl;
    this.logoImg.style.display = 'block';

    // Handle load errors gracefully - fall back to default branding
    this.logoImg.onerror = () => {
      logger.warn('Failed to load logo:', logoUrl);
      this.showDefaultBranding();
    };
  }

  /**
   * Hide any logo/branding (white-label mode for Pro+ users)
   */
  hideBranding(): void {
    if (this.logoContainer) {
      this.logoContainer.classList.add('hidden');
    }
  }

  /**
   * Show error message
   */
  setError(message: string): void {
    if (this.container) {
      this.container.classList.remove('hidden');
      // Build error UI safely without innerHTML to prevent XSS
      this.container.innerHTML = '';

      const wrapper = document.createElement('div');
      wrapper.style.cssText = 'color: #ff6b6b; font-size: 18px; text-align: center;';

      const icon = document.createElement('div');
      icon.style.cssText = 'font-size: 40px; margin-bottom: 20px;';
      icon.textContent = '⚠️';

      const title = document.createElement('strong');
      title.textContent = 'Error Loading Experience';

      const messageDiv = document.createElement('div');
      messageDiv.style.cssText = 'margin-top: 10px; font-size: 14px; opacity: 0.8;';
      messageDiv.textContent = message;

      const button = document.createElement('button');
      button.style.cssText = 'margin-top: 20px; padding: 10px 20px; background: rgba(255,255,255,0.2); border: 1px solid rgba(255,255,255,0.4); color: white; border-radius: 20px; cursor: pointer;';
      button.textContent = 'Try Again';
      button.onclick = () => window.location.reload();

      wrapper.appendChild(icon);
      wrapper.appendChild(title);
      wrapper.appendChild(document.createElement('br'));
      wrapper.appendChild(messageDiv);
      wrapper.appendChild(button);

      this.container.appendChild(wrapper);
    }
  }
}
